import type { ImageProvider } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Girando, Selo } from "../../components/ui.js";

/**
 * Quanto custa o modelo escolhido.
 *
 * A diferença entre o mais barato e o mais caro do catálogo de imagem passa de
 * dez vezes, e a escolha era feita às cegas: o campo mostrava só o nome. Num
 * evento de trezentas fotos, trocar de modelo sem ver o preço é a diferença
 * entre nove e quarenta dólares.
 *
 * Aparece só quando há um modelo selecionado — é o que evita quarenta e três
 * consultas de preço para mostrar uma.
 */
export function CaixaCusto({ provedor, modelo }: { provedor: ImageProvider; modelo: string }) {
  const preco = trpc.config.precoModelo.useQuery(
    { provedor, id: modelo },
    // O preço muda de mês em mês, não de minuto em minuto; e o backend já
    // guarda por uma hora.
    { enabled: modelo !== "", staleTime: 10 * 60 * 1000, retry: false },
  );

  if (modelo === "") return null;

  if (preco.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-peca border border-borda bg-superficie px-4 py-3 text-[0.8125rem] text-texto-fraco">
        <Girando className="size-4" />
        Consultando o preço deste modelo…
      </div>
    );
  }

  /**
   * Preço indisponível não é falha da tela.
   *
   * O modelo continua selecionável e a configuração continua salvável: o que
   * falta é uma informação de apoio. Um alerta vermelho aqui faria parecer que
   * a escolha está errada.
   */
  if (preco.isError || preco.data === undefined) {
    return (
      <div className="rounded-peca border border-borda bg-superficie px-4 py-3 text-[0.8125rem] text-texto-fraco">
        Não foi possível consultar o preço deste modelo agora.
      </div>
    );
  }

  const { linhas, precoTexto, provedores, maxReferencias } = preco.data;

  return (
    <div className="flex flex-col gap-2 rounded-peca border border-borda bg-superficie px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[0.8125rem] font-medium text-texto">Custo por geração</span>
        {provedores.map((p) => (
          <Selo key={p} tom="quieto">
            {p}
          </Selo>
        ))}
      </div>

      {linhas.length > 0 && (
        <ul className="flex flex-col gap-1">
          {linhas.map((l, i) => (
            <li
              key={`${l.cobranca}-${l.variante ?? ""}-${i}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-[0.8125rem]"
            >
              <span className="text-texto-suave">
                {nomeDaCobranca(l.cobranca)}
                {l.variante !== null && ` · ${l.variante.toUpperCase()}`}
              </span>
              <span className="font-mono text-texto">
                {emDolar(l.custoUsd)} por {nomeDaUnidade(l.unidade)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* O fal publica o preço como frase, não como tabela. Repetir a frase
          deles é mais honesto do que interpretá-la e arriscar errar o número. */}
      {precoTexto !== null && (
        <p className="text-[0.8125rem] leading-relaxed text-texto-suave">{precoTexto}</p>
      )}

      {linhas.length === 0 && precoTexto === null && (
        <p className="text-[0.8125rem] text-texto-fraco">
          Este modelo não publica preço em formato consultável.
        </p>
      )}

      {/* O que o operador não descobriria de outro jeito senão gerando: se o
          rosto da pessoa entra na conta. */}
      <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
        {maxReferencias === null
          ? "Aceita as cinco fotos de referência do atendimento."
          : maxReferencias === 0
            ? "Não aceita foto de referência: a imagem sai só do texto, sem o rosto da pessoa."
            : maxReferencias === 1
              ? "Aceita uma foto de referência: entra a do participante, as auxiliares ficam de fora."
              : `Aceita até ${maxReferencias} fotos de referência.`}
      </p>
    </div>
  );
}

function nomeDaCobranca(cobranca: string): string {
  const nomes: Record<string, string> = {
    output_image: "Imagem gerada",
    input_image: "Imagem enviada",
    input_reference: "Referência enviada",
  };

  return nomes[cobranca] ?? cobranca;
}

function nomeDaUnidade(unidade: string): string {
  const nomes: Record<string, string> = {
    image: "imagem",
    megapixel: "megapixel",
    token: "token",
  };

  return nomes[unidade] ?? unidade;
}

/**
 * Preço de imagem por token chega na casa dos centésimos de milésimo. Duas casas
 * mostrariam `US$ 0,00` para todos eles, que é pior do que um número comprido.
 */
function emDolar(valor: number): string {
  const casas = valor !== 0 && Math.abs(valor) < 0.01 ? 6 : 4;

  return valor.toLocaleString("pt-BR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: casas,
  });
}
